import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  TablePagination,
  Chip,
  Autocomplete,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import axios from 'axios';
import LoadingSpinner from './shared/LoadingSpinner';
import ConfirmDialog from './shared/ConfirmDialog';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    compatible_machines: []
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchProducts();
    fetchMachines();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      showSnackbar('Errore nel caricamento dei prodotti', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await axios.get('/api/machines');
      setMachines(response.data);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        code: product.code,
        description: product.description || '',
        compatible_machines: product.compatible_machines || []
      });
    } else {
      setSelectedProduct(null);
      setFormData({
        code: '',
        description: '',
        compatible_machines: []
      });
    }
    setErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProduct(null);
    setFormData({
      code: '',
      description: '',
      compatible_machines: []
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.code.trim()) {
      newErrors.code = 'Il codice prodotto è obbligatorio';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (selectedProduct) {
        await axios.put(`/api/products/${selectedProduct.id}`, formData);
        showSnackbar('Prodotto aggiornato con successo', 'success');
      } else {
        await axios.post('/api/products', formData);
        showSnackbar('Prodotto creato con successo', 'success');
      }
      handleCloseDialog();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      if (error.response?.data?.error) {
        showSnackbar(error.response.data.error, 'error');
        if (error.response.data.error.includes('code already exists')) {
          setErrors({ code: 'Questo codice prodotto esiste già' });
        }
      } else {
        showSnackbar('Errore nel salvataggio del prodotto', 'error');
      }
    }
  };

  const [orderConflict, setOrderConflict] = useState(null);

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/products/${selectedProduct.id}`);
      showSnackbar('Prodotto eliminato con successo', 'success');
      setConfirmOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      setConfirmOpen(false);

      if (error.response?.status === 409 && error.response?.data?.code === 'PRODUCT_HAS_ORDERS') {
        // Show detailed conflict dialog
        setOrderConflict({
          product: selectedProduct,
          orderCount: error.response.data.orderCount,
          orders: error.response.data.orders
        });
      } else if (error.response?.data?.error) {
        showSnackbar(error.response.data.error, 'error');
      } else {
        showSnackbar('Errore nell\'eliminazione del prodotto', 'error');
      }
    }
  };

  const handleForceDelete = async () => {
    if (!orderConflict?.product) return;
    try {
      // Delete all referencing orders first, then the product
      for (const order of orderConflict.orders) {
        await axios.delete(`/api/orders/${order.id}`);
      }
      await axios.delete(`/api/products/${orderConflict.product.id}`);
      showSnackbar('Prodotto e ordini collegati eliminati', 'success');
      setOrderConflict(null);
      fetchProducts();
    } catch (error) {
      showSnackbar('Errore nell\'eliminazione: ' + (error.response?.data?.error || error.message), 'error');
      setOrderConflict(null);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const filteredProducts = products.filter(product =>
    product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <InventoryIcon color="primary" fontSize="large" />
            <Typography variant="h4" component="h1">
              Anagrafica Prodotti
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nuovo Prodotto
          </Button>
        </Box>

        <Box mb={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Cerca per codice o descrizione..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Codice</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Macchine Compatibili</TableCell>
                <TableCell>Data Creazione</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      <Chip label={product.code} color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{product.description || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {product.compatible_machines && product.compatible_machines.length > 0 ? (
                          product.compatible_machines.map((machineId) => {
                            const machine = machines.find(m => m.id === machineId);
                            return machine ? (
                              <Chip
                                key={machineId}
                                label={machine.code}
                                size="small"
                                variant="outlined"
                                color="secondary"
                              />
                            ) : null;
                          })
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Tutte
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(product.created_at).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Modifica">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(product)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelectedProduct(product);
                            setConfirmOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredProducts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Nessun prodotto trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Codice Prodotto"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              error={!!errors.code}
              helperText={errors.code}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Descrizione"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            <Autocomplete
              multiple
              options={machines.filter(m => m.is_active)}
              disableCloseOnSelect
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              value={machines.filter(m => formData.compatible_machines.includes(m.id))}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  compatible_machines: newValue.map(m => m.id)
                });
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name} ({option.code})
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Macchine Compatibili"
                  placeholder="Seleziona macchine..."
                  margin="normal"
                  helperText="Opzionale - Macchine che possono produrre questo prodotto"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedProduct ? 'Aggiorna' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il prodotto "${selectedProduct?.code}"?`}
      />

      {/* Dialog conflitto ordini */}
      <Dialog
        open={!!orderConflict}
        onClose={() => setOrderConflict(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Prodotto collegato a ordini</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Il prodotto <strong>{orderConflict?.product?.code}</strong> non puo' essere eliminato
            perche' e' collegato a <strong>{orderConflict?.orderCount}</strong> ordine/i.
          </Alert>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Ordini collegati:</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Commessa</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Stato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderConflict?.orders?.map(o => (
                <TableRow key={o.id}>
                  <TableCell>#{o.id}</TableCell>
                  <TableCell>{o.commessa || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={o.status}
                      size="small"
                      color={o.status === 'completed' ? 'success' : o.status === 'in_production' ? 'primary' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Puoi eliminare prima gli ordini collegati e poi riprovare, oppure eliminare tutto insieme.
          </Typography>
          {orderConflict?.orders?.some(o => o.status === 'in_production' || o.status === 'in_progress') && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Attenzione: ci sono ordini in produzione. Non e' possibile eliminarli finche' sono attivi.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderConflict(null)}>Chiudi</Button>
          {!orderConflict?.orders?.some(o => o.status === 'in_production' || o.status === 'in_progress') && (
            <Button
              variant="contained"
              color="error"
              onClick={handleForceDelete}
            >
              Elimina tutto ({orderConflict?.orderCount} ordini + prodotto)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductManagement;